import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";

const SPECIALTIES = ["ICU", "ER", "Med-Surg", "OR", "Cath Lab", "Respiratory Therapy", "Physical Therapy", "Radiology"];
const STATES = ["AL","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"];

export default function Profile() {
  const { user, refresh } = useAuth();
  const [profile, setProfile] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      if (user.role === "clinician") {
        const { data } = await api.get("/clinicians/me");
        setProfile(data);
      } else if (user.role === "facility") {
        const { data } = await api.get("/facilities/me");
        setProfile(data);
      } else {
        setProfile({ email: user.email, name: user.name });
      }
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [user]);

  if (!profile) return <div className="text-slate-500">Loading profile…</div>;

  const save = async () => {
    setSaving(true);
    try {
      if (user.role === "clinician") {
        const payload = {
          name: profile.name, specialty: profile.specialty, license_state: profile.license_state,
          years_experience: Number(profile.years_experience), shift_preference: profile.shift_preference,
          pay_expectation: Number(profile.pay_expectation || 0), travel_ready: !!profile.travel_ready,
          phone: profile.phone, bio: profile.bio,
        };
        await api.put("/clinicians/me", payload);
      } else if (user.role === "facility") {
        const payload = {
          facility_name: profile.facility_name, facility_type: profile.facility_type,
          city: profile.city, state: profile.state, phone: profile.phone, description: profile.description,
        };
        await api.put("/facilities/me", payload);
      }
      await refresh();
      toast.success("Profile saved");
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };

  const upd = (k) => (e) => setProfile({ ...profile, [k]: e.target.value });

  return (
    <div className="space-y-6" data-testid="profile-page">
      <div>
        <div className="overline text-trital-red">Account</div>
        <h1 className="mt-1 font-display font-black text-3xl lg:text-4xl text-slate-900 tracking-tight">Profile</h1>
      </div>
      <div className="rounded-2xl bg-white border border-slate-200 p-6 shadow-card grid md:grid-cols-2 gap-4">
        {user.role === "clinician" && (
          <>
            <Field label="Full name" value={profile.name || ""} onChange={upd("name")} testid="profile-name" />
            <Field label="Phone" value={profile.phone || ""} onChange={upd("phone")} />
            <Select label="Specialty" value={profile.specialty} onChange={upd("specialty")} options={SPECIALTIES} />
            <Select label="License state" value={profile.license_state} onChange={upd("license_state")} options={STATES} />
            <Field label="Years experience" type="number" value={profile.years_experience || 0} onChange={upd("years_experience")} />
            <Select label="Shift preference" value={profile.shift_preference || "Day"} onChange={upd("shift_preference")} options={["Day", "Night", "Evening", "Rotating"]} />
            <Field label="Pay expectation ($/hr)" type="number" value={profile.pay_expectation || 0} onChange={upd("pay_expectation")} />
            <div className="flex items-center gap-2">
              <input id="travel_ready" type="checkbox" checked={!!profile.travel_ready} onChange={(e) => setProfile({ ...profile, travel_ready: e.target.checked })} />
              <label htmlFor="travel_ready" className="text-sm text-slate-700">Open to travel assignments</label>
            </div>
            <div className="md:col-span-2">
              <Field label="Bio" value={profile.bio || ""} onChange={upd("bio")} textarea />
            </div>
          </>
        )}
        {user.role === "facility" && (
          <>
            <Field label="Facility name" value={profile.facility_name || ""} onChange={upd("facility_name")} />
            <Field label="Facility type" value={profile.facility_type || ""} onChange={upd("facility_type")} />
            <Field label="City" value={profile.city || ""} onChange={upd("city")} />
            <Select label="State" value={profile.state} onChange={upd("state")} options={STATES} />
            <Field label="Phone" value={profile.phone || ""} onChange={upd("phone")} />
            <div className="md:col-span-2">
              <Field label="Description" value={profile.description || ""} onChange={upd("description")} textarea />
            </div>
          </>
        )}
        {(user.role === "recruiter" || user.role === "admin") && (
          <div className="md:col-span-2 text-slate-600">
            <div><span className="overline text-slate-500">Name</span><div className="font-semibold text-slate-900">{user.name}</div></div>
            <div className="mt-2"><span className="overline text-slate-500">Email</span><div className="font-semibold text-slate-900">{user.email}</div></div>
            <div className="mt-2"><span className="overline text-slate-500">Role</span><div className="font-semibold text-trital-navy uppercase">{user.role}</div></div>
          </div>
        )}
        {(user.role === "clinician" || user.role === "facility") && (
          <div className="md:col-span-2">
            <button onClick={save} disabled={saving} className="px-5 py-2.5 rounded-full bg-trital-navy text-white font-semibold btn-lift hover:bg-trital-navy-deep disabled:opacity-60" data-testid="profile-save">{saving ? "Saving…" : "Save changes"}</button>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, textarea, type = "text", value, onChange, testid }) {
  return (
    <div>
      <label className="text-xs font-bold uppercase tracking-wider text-slate-600">{label}</label>
      {textarea ? (
        <textarea rows={4} value={value} onChange={onChange} className="mt-1 w-full px-4 py-3 rounded-xl border border-slate-300 focus:ring-2 focus:ring-trital-navy/30 focus:border-trital-navy outline-none" data-testid={testid} />
      ) : (
        <input type={type} value={value} onChange={onChange} className="mt-1 w-full px-4 py-3 rounded-xl border border-slate-300 focus:ring-2 focus:ring-trital-navy/30 focus:border-trital-navy outline-none" data-testid={testid} />
      )}
    </div>
  );
}
function Select({ label, value, onChange, options }) {
  return (
    <div>
      <label className="text-xs font-bold uppercase tracking-wider text-slate-600">{label}</label>
      <select value={value} onChange={onChange} className="mt-1 w-full px-4 py-3 rounded-xl border border-slate-300 focus:ring-2 focus:ring-trital-navy/30 focus:border-trital-navy outline-none">
        {options.map((o) => <option key={o}>{o}</option>)}
      </select>
    </div>
  );
}
